import React, { useCallback, useMemo, useState } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import AppLayoutToolbar from "@amzn/awsui-components-react/polaris/app-layout-toolbar";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import { useSelector } from "react-redux";

import { DEFAULT_LEFT_NAV_PATH } from "./shellRoutes";
import { LeftNav } from "./LeftNav";
import { AppBreadcrumbs } from "./AppBreadcrumbs";
import { FilterBar } from "../filterBar/FilterBar";
import { PorAsinListTable } from "../porAsinList/PorAsinListTable";
import { BaselineTable } from "../dateBaseline/baselineTable";
import { ValidationDashboardPage } from "../validation/ValidationDashboardPage";
import { OneClickLaunchTable } from "../oneClickLaunch/oneClickLaunchTable";
import { StamperWizard } from "../stampUtils/StamperWizard";
import { LoomAgentAssistantWidget } from "../../components/loom-agent-assistant/LoomAgentAssistantWidget";
import { selectIsLoomAgentAssistantEnabled } from "../userInfo/userInfoSlice";
import { InitializeRum } from "../rum/initializeRum";
import { Notification } from "../notification/notification";
import { CacheableNotification } from "../notification/cacheableNotification";
import { StaleDataAlert } from "../validation/StaleDataAlert";
import { V2Toggle } from "../app/V2Toggle";

const AGENT_ASSISTANT_DRAWER_ID = "agent-assistant";

const AGENT_ASSISTANT_DRAWER_STYLE: React.CSSProperties = {
  height: "100%",
  width: "100%",
  boxSizing: "border-box",
};

/**
 * App Shell root. FilterBar mounts above `<Routes/>` so it survives
 * intra-shell nav without remount. Drawer config mirrors Legacy.
 */
export const AppShell: React.FC = () => {
  const isLoomAgentAssistantEnabled = useSelector(
    selectIsLoomAgentAssistantEnabled
  );

  const [activeDrawerId, setActiveDrawerId] = useState<string | null>(null);
  const handleDrawerChange = useCallback(
    ({ detail }: { detail: { activeDrawerId: string | null } }) => {
      setActiveDrawerId(detail.activeDrawerId);
    },
    []
  );

  const drawers = useMemo(
    () =>
      isLoomAgentAssistantEnabled
        ? [
            {
              id: AGENT_ASSISTANT_DRAWER_ID,
              ariaLabels: {
                drawerName: "Loom Agent Assistant",
                closeButton: "Close Loom Agent Assistant",
                triggerButton: "Open Loom Agent Assistant",
                resizeHandle: "Resize Loom Agent Assistant panel",
              },
              trigger: { iconName: "gen-ai" as const },
              resizable: true,
              defaultSize: 400,
              content: (
                <div
                  data-testid="agent-assistant-drawer"
                  style={AGENT_ASSISTANT_DRAWER_STYLE}
                >
                  <LoomAgentAssistantWidget />
                </div>
              ),
            },
          ]
        : [],
    [isLoomAgentAssistantEnabled]
  );

  return (
    <BrowserRouter>
      <V2Toggle />
      <AppLayoutToolbar
        data-testid="app-shell-layout"
        navigation={<LeftNav />}
        breadcrumbs={<AppBreadcrumbs />}
        contentType="table"
        toolsHide={true}
        activeDrawerId={activeDrawerId}
        onDrawerChange={handleDrawerChange}
        drawers={drawers}
        content={<AppShellRoutedContent />}
      />
    </BrowserRouter>
  );
};

// FilterBar drives ASIN-list filtering; Stamper Tool is a distinct
// workflow that doesn't consume that state, so we skip the bar on
// `/stamper` to avoid a stale filter UI above an unrelated wizard.
const PATHS_WITHOUT_FILTER_BAR: ReadonlySet<string> = new Set(["/stamper"]);

const AppShellRoutedContent: React.FC = () => {
  const location = useLocation();
  const showFilterBar = !PATHS_WITHOUT_FILTER_BAR.has(location.pathname);

  return (
    <div data-testid="app-shell-content">
      <InitializeRum />
      <SpaceBetween size="l" direction="vertical">
        {showFilterBar && <FilterBar />}
        <div data-testid="notifications-container">
          <Notification />
          <CacheableNotification />
          <StaleDataAlert />
        </div>
        <Routes>
          <Route
            path="/"
            element={<Navigate to={DEFAULT_LEFT_NAV_PATH} replace />}
          />
          <Route path="/search" element={<PorAsinListTable />} />
          <Route path="/baseline" element={<BaselineTable />} />
          <Route path="/validation" element={<ValidationDashboardPage />} />
          <Route path="/launch" element={<OneClickLaunchTable />} />
          <Route path="/stamper" element={<StamperWizard />} />
          <Route
            path="*"
            element={<Navigate to={DEFAULT_LEFT_NAV_PATH} replace />}
          />
        </Routes>
      </SpaceBetween>
    </div>
  );
};

export default AppShell;
